import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, lastValueFrom } from 'rxjs';
import { ClientModifyResponse } from '../models/ClientResponse';
import { ClientConfigurationView } from 'app/models/ClientConfigurationView';
import { environment } from 'environments/environment';
import { Client } from 'app/models/Client';
import { AvailableForm } from 'app/models/AvailableForm';
import { Configuration } from 'app/models/Configuration';
import { BudgetConfiguration } from 'app/models/BudgetConfiguration';
import { Contact } from 'app/models/Contact';
import { ClientTaxInformation } from 'app/models/ClientTaxInformation';
import { shareReplay, tap, map } from 'rxjs/operators';
import { ClientLogoDetail } from 'app/models/ClientLogoDetail';
import { POOpenAmountsConfigurationView } from 'app/models/purchasing/POOpenAmountsConfigurationView';
import { LegacyMappingService } from './LegacyMappingService';
import { BudgetToleranceConfigurationView } from 'app/models/purchasing/BudgetToleranceConfigurationView';
import {
	ClientConfigurationImportConfirmRequest,
	ClientConfigurationImportHistoryEntry,
	ClientConfigurationImportPreview,
	ClientConfigurationImportResult,
} from 'app/models/client-configuration-import/ClientConfigurationImport';

@Injectable({
	providedIn: 'root',
})
export class ClientsService {
	private cachedClientFormsConfig = new Map<string, Observable<Configuration>>();
	private readonly baseUrl: string;

	constructor(private readonly httpClient: HttpClient, private readonly legacyMappingService: LegacyMappingService) {
		this.baseUrl = `${environment.apiUrl}clients`;
	}

	getClientList(): Observable<Client[]> {
		return this.httpClient
			.get<Client[]>(`${this.baseUrl}`)
			.pipe(tap((items) => sessionStorage.setItem('clientList', JSON.stringify(items))));
	}

	createClient(item: Client): Observable<ClientModifyResponse> {
		return this.httpClient.post<ClientModifyResponse>(`${this.baseUrl}`, item).pipe();
	}

	getClient(clientId: string): Observable<Client> {
		return this.httpClient.get<Client>(`${this.baseUrl}/${clientId}`).pipe();
	}

	getClientForms(clientId: string, isEnabled?: boolean): Observable<AvailableForm[]> {
		return this.httpClient.get<AvailableForm[]>(`${this.baseUrl}/${clientId}/${isEnabled}/forms/`).pipe();
	}

	getClientEnabledFormsConfig(clientId: string): Observable<Configuration> {
		if (!this.cachedClientFormsConfig.has(clientId)) {
			this.cachedClientFormsConfig.set(
				clientId,
				this.httpClient
					.get<Configuration>(`${this.baseUrl}/${clientId}/client-enabled-forms`)
					.pipe(shareReplay({ bufferSize: 1, refCount: true }))
			);
		}
		return this.cachedClientFormsConfig.get(clientId);
	}

	getClientConfigurations(clientId: string): Observable<Configuration[]> {
		return this.httpClient.get<Configuration[]>(`${this.baseUrl}/${clientId}/configurations`).pipe();
	}

	getClientFeatures(clientId: string): Observable<ClientConfigurationView> {
		return this.httpClient.get<ClientConfigurationView>(`${this.baseUrl}/${clientId}/clientConfigurations`).pipe();
	}

	updateClient(item: Client): Observable<ClientModifyResponse> {
		return this.httpClient.put<ClientModifyResponse>(`${this.baseUrl}/${item.id}`, item).pipe();
	}

	updateClientConfiguration(clientId: string, item: Configuration): Observable<any> {
		return this.httpClient.put<any>(`${this.baseUrl}/${clientId}/configurations`, item).pipe();
	}

	updateAssignmentConfiguration(clientId: string, item: Configuration): Observable<any> {
		return this.httpClient.put<any>(`${this.baseUrl}/${clientId}/assignment-configurations`, item).pipe();
	}

	updateWorkflowConfiguration(clientId: string, items: Configuration[], formType: string): Observable<any> {
		return this.httpClient
			.post<any>(`${this.baseUrl}/${clientId}/workflow-configurations?formType=${formType}`, items)
			.pipe();
	}

	getWorkflowConfiguration(clientId: string, formType: string): Observable<Configuration[]> {
		return this.httpClient
			.get<Configuration[]>(`${this.baseUrl}/${clientId}/workflow-configurations?formType=${formType}`)
			.pipe();
	}

	checkWorkflowConfigured(clientId: string, formType: string): Observable<boolean> {
		let effectiveFormType = formType;
		const legacyMapping = this.legacyMappingService.getFormSettingsMapping(formType);
		if (legacyMapping) {
			effectiveFormType = legacyMapping.value;
		}

		return this.getWorkflowConfiguration(clientId, effectiveFormType).pipe(
			map((configs) => {
				if (!configs || configs.length === 0) {
					return false;
				}

				// The engine and the admin write path both build this key as <entity name> + 'FormApprovalCount'
				// unconditionally (IWorkflowService / admin-new-form-settings), so a form whose entity name itself
				// ends in 'Form' stores a doubled segment (JuryCheckRegistryFormFormApprovalCount), while a caller
				// passing <entity> + 'Form' (TuitionReimbursementPaymentForm) stores the single spelling. Accept
				// both, still by exact key: no two form entities differ by exactly a 'Form' suffix, so neither
				// candidate can ever belong to a different form family and PI-1504 cannot recur through this path.
				const candidateConfigTypes = [`${effectiveFormType}FormApprovalCount`.toLowerCase()];
				if (effectiveFormType.toLowerCase().endsWith('form')) {
					candidateConfigTypes.push(`${effectiveFormType}ApprovalCount`.toLowerCase());
				}
				const approvalCountConfig = configs.find((c) =>
					candidateConfigTypes.includes(c.configurationType?.toLowerCase())
				);

				if (!approvalCountConfig) {
					return false;
				}

				const approvalCount = parseInt(approvalCountConfig.value, 10);
				return !isNaN(approvalCount) && approvalCount > 0;
			})
		);
	}

	saveForms(clientId: string, forms: AvailableForm[]): Observable<any> {
		return this.httpClient.put<any>(`${this.baseUrl}/${clientId}/forms`, forms).pipe();
	}

	getClientBudgetConfig(clientId: string): Observable<BudgetConfiguration> {
		return this.httpClient.get<BudgetConfiguration>(`${this.baseUrl}/${clientId}/budget/config`).pipe();
	}

	saveClientBudgetConfig(clientId: string, budgetConfig: BudgetConfiguration): Observable<any> {
		return this.httpClient.put<any>(`${this.baseUrl}/${clientId}/budget/config`, budgetConfig).pipe();
	}

	getClientPOConfig(clientId: string): Observable<POOpenAmountsConfigurationView> {
		return this.httpClient
			.get<POOpenAmountsConfigurationView>(`${this.baseUrl}/${clientId}/purchasing/po-config`)
			.pipe();
	}

	saveClientPOConfig(
		clientId: string,
		item: POOpenAmountsConfigurationView
	): Observable<POOpenAmountsConfigurationView> {
		return this.httpClient
			.put<POOpenAmountsConfigurationView>(`${this.baseUrl}/${clientId}/purchasing/po-config`, item)
			.pipe();
	}

	hasHRModule(clientId: string): Observable<any> {
		return this.httpClient.get<any>(`${this.baseUrl}/${clientId}/has-hr-module`).pipe();
	}

	getClientCashieringConfig(clientId: string): Observable<any> {
		return this.httpClient.get<any>(`${this.baseUrl}/${clientId}/cashiering-config`).pipe();
	}

	saveClientCashieringConfig(clientId: string, item: any): Observable<any> {
		return this.httpClient.put<any>(`${this.baseUrl}/${clientId}/cashiering-config`, item).pipe();
	}

	getDuplicateInvoiceWarningConfiguration(clientId: string): Observable<any> {
		return this.httpClient
			.get<any>(`${this.baseUrl}/${clientId}/purchasing/GetDuplicateInvoiceWarningConfiguration`)
			.pipe();
	}

	saveDuplicateInvoiceWarningConfiguration(clientId: string, item: any): Observable<any> {
		return this.httpClient
			.put<any>(`${this.baseUrl}/${clientId}/purchasing/UpdateDuplicateInvoiceWarningConfiguration`, item)
			.pipe();
	}

	getCashBondDuplicateConfiguration(clientId: string): Observable<any> {
		return this.httpClient.get<any>(`${this.baseUrl}/${clientId}/purchasing/GetCashBondDuplicateConfiguration`).pipe();
	}

	saveCashBondDuplicateConfiguration(clientId: string, item: any): Observable<any> {
		return this.httpClient
			.put<any>(`${this.baseUrl}/${clientId}/purchasing/UpdateCashBondDuplicateConfiguration`, item)
			.pipe();
	}

	getClientBudgetToleranceConfig(clientId: string): Observable<any> {
		return this.httpClient.get<any>(`${this.baseUrl}/${clientId}/payments/budget-tolerance-config`).pipe();
	}

	saveClientBudgetToleranceConfig(clientId: string, item: any): Observable<any> {
		return this.httpClient.put<any>(`${this.baseUrl}/${clientId}/payments/budget-tolerance-config`, item).pipe();
	}

	getPositivePayConfig(clientId: string): Observable<any> {
		return this.httpClient.get<any>(`${this.baseUrl}/${clientId}/payments/positive-pay-Config`).pipe();
	}

	savePositivePayConfig(clientId: string, item: any): Observable<any> {
		return this.httpClient.put<any>(`${this.baseUrl}/${clientId}/payments/positive-pay-Config`, item).pipe();
	}

	generatePositivePay(clientId: string): Observable<any> {
		return this.httpClient.get<any>(`${this.baseUrl}/${clientId}/payments/generate-positive-pay`).pipe();
	}

	downloadPositivePayFile(clientId: string, fileName: string): Observable<Blob> {
		return this.httpClient.get<Blob>(`${this.baseUrl}/${clientId}/payments/download-positive-pay/${fileName}`, {
			responseType: 'blob' as any,
		});
	}

	updateClientContact(clientId: string, item: Contact): Observable<any> {
		return this.httpClient.put(`${this.baseUrl}/${clientId}/contact`, item).pipe();
	}

	updateClientTaxInformation(clientId: string, item: ClientTaxInformation): Observable<any> {
		return this.httpClient.put(`${this.baseUrl}/${clientId}/tax-information`, item).pipe();
	}
	updateClientLogoDetail(clientId: string, item: ClientLogoDetail): Observable<any> {
		return this.httpClient.put(`${this.baseUrl}/${clientId}/client-logo-configuration`, this.setupRequest(item)).pipe();
	}

	getClientContact(clientId: string): Observable<Contact> {
		return this.httpClient.get<Contact>(`${this.baseUrl}/${clientId}/contact`).pipe();
	}

	getClientTaxInformation(clientId: string): Observable<ClientTaxInformation> {
		return this.httpClient.get<ClientTaxInformation>(`${this.baseUrl}/${clientId}/tax-information`).pipe();
	}

	getClientLogoConfiguration(clientId: string): Observable<ClientLogoDetail> {
		return this.httpClient.get<ClientLogoDetail>(`${this.baseUrl}/${clientId}/client-logo-configuration`).pipe();
	}

	createTaxBanditBusiness(clientId: string): Observable<ClientTaxInformation> {
		return this.httpClient.post<ClientTaxInformation>(`${this.baseUrl}/${clientId}/taxbandits/business`, {}).pipe();
	}

	getAchBatchConfig(clientId: string): Observable<any> {
		return this.httpClient.get<any>(`${this.baseUrl}/${clientId}/payments/ach-batch-config`).pipe();
	}

	saveAchBatchConfig(clientId: string, item: any): Observable<any> {
		return this.httpClient.put<any>(`${this.baseUrl}/${clientId}/payments/ach-batch-config`, item).pipe();
	}
	saveFileNameFormatAchBatchConfig(clientId: string, item: any): Observable<any> {
		return this.httpClient.put<any>(`${this.baseUrl}/${clientId}/payments/ach-file-name-format-config`, item).pipe();
	}
	generateAchBatch(
		clientId: string,
		request: { sourceIds: string[]; bankAccountId?: string | null; overrideConfirmed?: boolean }
	): Observable<any> {
		return this.httpClient.post<any>(`${this.baseUrl}/${clientId}/payments/generate-ach-batch`, request).pipe();
	}

	downloadAchBatchFile(clientId: string, fileName: string): Observable<Blob> {
		const encoded = encodeURIComponent(fileName);
		return this.httpClient.get<Blob>(`${this.baseUrl}/${clientId}/payments/download-ach-batch/${encoded}`, {
			responseType: 'blob' as any,
		});
	}

	getMileageRate(clientId: string, configurationType: string, defaultRate: any): Observable<number> {
		return this.httpClient.get<number>(
			`${this.baseUrl}/${clientId}/${configurationType}/mileage-rate?defaultRate=${defaultRate}`
		);
	}
	getClientDailyFeesImportConfig(clientId: string): Observable<any> {
		return this.httpClient.get<any>(`${this.baseUrl}/${clientId}/daily-fees-import-config`).pipe();
	}
	saveClientDailyFeesImportConfig(clientId: string, item: any): Observable<any> {
		return this.httpClient.put<any>(`${this.baseUrl}/${clientId}/daily-fees-import-config`, item).pipe();
	}
	getClientInvoiceItemPriceAdjustmentConfig(clientId: string): Observable<any> {
		return this.httpClient.get<any>(`${this.baseUrl}/${clientId}/payments/invoice-item-price-adjustment-config`).pipe();
	}
	saveClientInvoiceItemPriceAdjustmentConfig(clientId: string, item: any): Observable<any> {
		return this.httpClient
			.put<any>(`${this.baseUrl}/${clientId}/payments/invoice-item-price-adjustment-config`, item)
			.pipe();
	}
	getDuplicateVendorWarningConfiguration(clientId: string): Observable<any> {
		return this.httpClient
			.get<any>(`${this.baseUrl}/${clientId}/purchasing/duplicate-vendor-warning-configuration`)
			.pipe();
	}
	saveDuplicateVendorWarningConfiguration(clientId: string, item: any): Observable<any> {
		return this.httpClient
			.put<any>(`${this.baseUrl}/${clientId}/purchasing/duplicate-vendor-warning-configuration`, item)
			.pipe();
	}
	getAllowSaveOnlyConfig(clientId: string): Observable<any> {
		return this.httpClient.get<any>(`${this.baseUrl}/${clientId}/allow-save-only`);
	}
	getFormUrgentConfiguration(clientId: string): Observable<any> {
		return this.httpClient.get<any>(`${this.baseUrl}/${clientId}/form-urgent-configuration`).pipe();
	}
	getReimbursementMileageRate(clientId: string): Observable<number> {
		return this.httpClient.get<number>(`${this.baseUrl}/${clientId}/reimbursement-mileage-rate`);
	}

	getClientSharingConfigurations(): Observable<any[]> {
		return this.httpClient.get<any[]>(`${this.baseUrl}/sharing-configurations/`);
	}

	saveClientSharingConfigurations(configurations: any[]): Observable<any> {
		return this.httpClient.post<any>(`${this.baseUrl}/sharing-configurations/save`, configurations);
	}

	deleteClientSharingConfiguration(id: string): Observable<any> {
		return this.httpClient.delete<any>(`${this.baseUrl}/${id}`);
	}

	isAnyVendorSharingEnabled(parentClientId: string): Observable<boolean> {
		return this.httpClient.get<boolean>(`${this.baseUrl}/vendor-sharing-any?parentClientId=${parentClientId}`);
	}
	getInvoiceNumberRequiredConfig(clientId: string): Observable<any> {
		return this.httpClient.get<any>(`${this.baseUrl}/${clientId}/invoice-number-required`);
	}
	getPoCcEmailConfiguration(clientId: string) {
		return this.httpClient.get<any>(`${this.baseUrl}/${clientId}/purchasing/po-cc-email-config`).pipe();
	}
	savePoCcEmailConfiguration(clientId: string, data: any) {
		return this.httpClient.put<any>(`${this.baseUrl}/${clientId}/purchasing/po-cc-email-config`, data).pipe();
	}
	getChildClient(parentclientId: string): Observable<any> {
		return this.httpClient
			.get<any>(`${this.baseUrl}/sharing-configurations/ChildClient?parentClientId=${parentclientId}`)
			.pipe();
	}
	getCurrentFiscalYear(clientId: string): Observable<any> {
		return this.httpClient.get<any>(`${this.baseUrl}/${clientId}/get-current-fiscal-year`).pipe();
	}
	getClientConfigByType(clientId: string, configurationType: string): Observable<any> {
		return this.httpClient.get<any>(`${this.baseUrl}/${clientId}/get-client-config-by-type/${configurationType}`);
	}
	clearClientFormsConfig() {
		this.cachedClientFormsConfig.clear();
	}

	private setupRequest(request: any) {
		const formData: FormData = new FormData();
		formData.append('logo', request.logo);
		formData.append('model', JSON.stringify(request));
		return formData;
	}
	getClientConfigurationByType(clientId: string, configurationType: string): Observable<Configuration> {
		return this.httpClient.get<Configuration>(`${this.baseUrl}/${clientId}/configuration/${configurationType}`);
	}
	getClientBudgetTransferConfig(clientId: string): Observable<any> {
		return this.httpClient.get<any>(`${this.baseUrl}/${clientId}/budget/budget-transfer-config`).pipe();
	}
	saveClientBudgetTransferConfig(clientId: string, item: any): Observable<any> {
		return this.httpClient.put<any>(`${this.baseUrl}/${clientId}/budget/budget-transfer-config`, item).pipe();
	}

	exportClientConfiguration(clientId: string): Observable<Blob> {
		return this.httpClient.get(`${this.baseUrl}/${clientId}/export-configuration`, {
			responseType: 'blob',
		});
	}

	getClientConfigurationImportHistory(clientId: string): Observable<ClientConfigurationImportHistoryEntry[]> {
		return this.httpClient.get<ClientConfigurationImportHistoryEntry[]>(
			`${this.baseUrl}/${clientId}/import-configuration/history`
		);
	}

	previewImportClientConfiguration(file: File): Observable<ClientConfigurationImportPreview> {
		const formData = new FormData();
		formData.append('file', file, file.name);
		return this.httpClient.post<ClientConfigurationImportPreview>(
			`${this.baseUrl}/import-configuration/preview`,
			formData
		);
	}

	confirmImportClientConfiguration(
		request: ClientConfigurationImportConfirmRequest
	): Observable<ClientConfigurationImportResult> {
		return this.httpClient.post<ClientConfigurationImportResult>(
			`${this.baseUrl}/import-configuration/confirm`,
			request
		);
	}

	downloadImportPreviewValidationExcel(preview: ClientConfigurationImportPreview): Observable<Blob> {
		return this.httpClient.post(`${this.baseUrl}/import-configuration/preview-validation-excel`, preview, {
			responseType: 'blob',
		});
	}

	downloadImportDiscrepancyExcel(result: ClientConfigurationImportResult): Observable<Blob> {
		return this.httpClient.post(`${this.baseUrl}/import-configuration/discrepancy-excel`, result, {
			responseType: 'blob',
		});
	}

	uploadPortalLogo(clientId: string, file: File): Observable<{ logoUrl: string }> {
		const formData = new FormData();
		formData.append('file', file, file.name);
		return this.httpClient.post<{ logoUrl: string }>(`${this.baseUrl}/${clientId}/portal-branding/logo`, formData);
	}

	uploadPortalHeroImages(clientId: string, files: File[]): Observable<{ urls: string[] }> {
		const formData = new FormData();
		for (const file of files) {
			formData.append('files', file, file.name);
		}
		return this.httpClient.post<{ urls: string[] }>(
			`${this.baseUrl}/${clientId}/portal-branding/hero-images`,
			formData
		);
	}
	/**
	 * Read a boolean client configuration by type, parsing the stored string
	 * value ("true"/"false") into a boolean. Returns defaultValue when the
	 * configuration is missing, unparseable, or the request fails — so callers
	 * can await it without guarding against an unhandled rejection. Reusable for
	 * any on/off client configuration, not specific to a single feature.
	 */
	async getBooleanConfiguration(clientId: string, configurationType: string, defaultValue = false): Promise<boolean> {
		try {
			const configuration = await lastValueFrom(this.getClientConfigurationByType(clientId, configurationType));
			const value = (configuration?.value ?? '').toString().trim().toLowerCase();
			if (value === 'true') {
				return true;
			}
			if (value === 'false') {
				return false;
			}
			return defaultValue;
		} catch {
			return defaultValue;
		}
	}
}
